import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { golf } from './enums';
import { FormattedMessage } from 'react-intl';
import golfToEid from 'golf-to-eid';
import Link from '@ux/link';
import LinkArrow from '@ux/icon/link-arrow';

export class ParentPanel extends Component {
  constructor() {
    super(...arguments);
    this.renderParents = this.renderParents.bind(this);

    this.focusables = [];

    this.state = {
      ...this.state,
      hovered: false
    };

    this.createFocusRefs = this.createFocusRefs.bind(this);
    this.focusNext = this.focusNext.bind(this);
    this.focusPrevious = this.focusPrevious.bind(this);
  }

  /**
   * Focus on the next link in the shown panel
   * @private
   */
  focusNext() {
    if (this.focusables.length < 1) return;

    const newIndex = (1 + this.focusables.indexOf(document.activeElement)) % (this.focusables.length);
    this.focusables[newIndex].focus();
  }

  /**
   * Focus on the previous link in the shown panel
   * @private
   */
  focusPrevious() {
    if (this.focusables.length < 1) return;

    const len = this.focusables.length;
    const newIndex = (len - 1 + this.focusables.indexOf(document.activeElement)) % len;
    this.focusables[newIndex].focus();
  }

  /**
   * Store refs to the first and last focusable nav items
   * @private
   * @param {HTMLElement} btn DOM node to be stored in ref
   */
  createFocusRefs(btn) {
    this.focusables.push(btn);
  }

  /**
   * Render top level headings for nav. If the nav has children,
   * the heading will trigger expansion of child panel
   *
   * @private
   * @param {Object[]} nav Array of objects for generating parent items
   * @returns {React.ReactElement[]} List items for parent nav items
   */
  renderParents(nav) {
    const props = this.props;

    return nav.map((item) => {
      if (item.onlyallproducts) {
        return;
      }
      const { children, title, href, id } = item;
      if (title) {
        const eid = golfToEid(id, {
          prefix: props.eidPrefix,
          postfix: 'link.click'
        });
        if (children && children.length > 0) {
          // list item that will trigger a menu
          return (
            <li
              className='parent-with-child'
              key={ id }
              data-eid={ eid }>
              <button
                tabIndex={ props.tabIndex }
                onClick={ () => props.onParentClick(item) }
                onMouseEnter={ () =>
                  this.setState({
                    hovered: true
                  })
                }
                onMouseLeave={ () =>
                  this.setState({
                    hovered: false
                  })
                }
                ref={ this.createFocusRefs }>
                <span>{ title }</span>
                <LinkArrow aria-hidden='true'/>
              </button>
            </li>
          );
        } else if (href) {
          // Plain link
          return (
            <li key={ id }>
              <Link
                href={ href }
                data-eid={ eid }
                urlArgs={ this.props.urlArgs }
                onClicked={ props.onClose }
                className='single-parent-link'
                tabIndex={ props.tabIndex }
                onMouseEnter={ () =>
                  this.setState({
                    hovered: true
                  })
                }
                onMouseLeave={ () =>
                  this.setState({
                    hovered: false
                  })
                }>
                <span>{ title }</span>
              </Link>
            </li>
          );
        }
      }
    });
  }

  render() {
    const { props, state } = this;

    return (
      <div
        className={ classnames('parent-menu-panel', {
          'open': props.open,
          'product-flyout-hovered-menu': props.open && state.hovered
        }) }>
        <div
          className='container-fluid menu-scroller'
          onScroll={ this.props.onScroll }
          ref={ r => {
            this.outerDiv = r;
          } }>
          <div
            className='parent-navigation'
            ref={ r => {
              this.innerDiv = r;
            } }>
            <div>
              <div className='product-flyout-heading'>
                <FormattedMessage id={ golf.product } />
              </div>
              <ul
                className='list-unstyled parent-link-items parent-product-nav'>
                { this.renderParents(props.productNav) }
              </ul>
            </div>
            <div>
              <div className='product-flyout-heading'>
                <FormattedMessage id={ golf.discover } />
              </div>
              <ul
                className='list-unstyled parent-link-items parent-discover-nav'>
                { this.renderParents(props.discoverNav) }
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

ParentPanel.propTypes = {
  productNav: PropTypes.arrayOf(PropTypes.object).isRequired,
  discoverNav: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func,
  onParentClick: PropTypes.func,
  open: PropTypes.bool,
  tabIndex: PropTypes.number,
  eidPrefix: PropTypes.string,
  urlArgs: PropTypes.object,
  onScroll: PropTypes.func
};

ParentPanel.defaultProps = {
  onParentClick: () => {},
  open: false
};

export default ParentPanel;
